'use client';

import { LAYER_NAMES, type LayerName } from '@slims/shared/energy';
import { PodLayerColors, PodLayerLabels, PodLayersChart, type PodLayerPoint } from '../../charts';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '../../molecules/dialog/dialog';

/**
 * Esploso per punto di misura: i quattro livelli energetici di un POD su un
 * giorno, sovrapposti.
 *
 * Le viste aggregate mostrano un solo numero per slot — quello che ha vinto la
 * riconciliazione — e nascondono da dove viene. Qui si vede la provenienza: la
 * copertura di ciascun livello, dove il certificato GSE non è ancora arrivato,
 * dove la telemetria ha buchi, e di quanto la stima si scosta dal reale nei
 * punti in cui si possono confrontare.
 *
 * Riceve dati e riporta intenzione: nessuna chiamata API, nessuno store.
 */
/** Slot coperti da ciascun livello, sul totale del giorno (92, 96 o 100). */
export type PodExplodeCoverage = Record<LayerName, number> & { totalSlots: number };

export interface PodExplodeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Codice del punto di misura — il POD per M1, l'UPN per M2 */
	puntoMisura: string;
	/** Codice POD del punto di connessione */
	pod: string;
	/** Etichetta della direzione, es. 'Immissione (M2)' */
	direzione: string;
	/** Giorno mostrato, già formattato per l'utente */
	giorno: string;
	data: PodLayerPoint[];
	coverage: PodExplodeCoverage;
	unit?: string;
	loading?: boolean;
	/** Navigazione fra i giorni; assente = frecce nascoste */
	onPrevDay?: () => void;
	onNextDay?: () => void;
	nextDayDisabled?: boolean;
}

const PERCENT = (n: number, total: number) =>
	total > 0 ? `${Math.round((n / total) * 100)}%` : '-';

/** Riga di copertura: quanto di quel giorno ciascun livello riesce a coprire. */
function CoverageRow({ coverage }: { coverage: PodExplodeCoverage }) {
	// Generata da `LAYERS`: quattro card scritte a mano erano quattro punti dove
	// l'elenco poteva divergere dall'ordine su cui la riconciliazione gira.
	const rows = LAYER_NAMES.map((key) => ({
		key,
		label: PodLayerLabels[key],
		value: coverage[key],
		color: PodLayerColors[key],
		// GSE e reale restano visibili anche a zero: la loro assenza è
		// informazione, non rumore. Il tipico copre sempre tutto e lo stimato lo
		// segue, quindi a zero vorrebbero dire solo «curva tipica non generata».
	})).filter((r) => r.value > 0 || r.key === 'gse' || r.key === 'reale');

	return (
		<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
			{rows.map((r) => (
				<div
					key={r.key}
					className="rounded-md border border-border-secondary bg-bg-secondary p-2.5"
				>
					<div className="mb-1 flex items-center gap-1.5">
						<span
							className="size-2.5 shrink-0 rounded-sm"
							style={{ backgroundColor: r.color }}
							aria-hidden
						/>
						<span className="text-[11px] text-fg-secondary">{r.label}</span>
					</div>
					<div className="font-semibold text-fg text-lg leading-tight">
						{PERCENT(r.value, coverage.totalSlots)}
						<span className="ml-1 font-normal text-[11px] text-fg-secondary">
							{r.value}/{coverage.totalSlots} slot
						</span>
					</div>
				</div>
			))}
		</div>
	);
}

export function PodExplodeDialog({
	open,
	onOpenChange,
	puntoMisura,
	pod,
	direzione,
	giorno,
	data,
	coverage,
	unit = 'kWh',
	loading,
	onPrevDay,
	onNextDay,
	nextDayDisabled,
}: PodExplodeDialogProps) {
	// 92 o 100 slot: il giorno di cambio ora, che vale la pena dire esplicitamente
	// invece di lasciare che il grafico sembri semplicemente più corto o più lungo.
	const dstNote =
		coverage.totalSlots === 92
			? 'Giorno di inizio ora legale: 23 ore, 92 slot.'
			: coverage.totalSlots === 100
				? 'Giorno di fine ora legale: 25 ore, 100 slot - l’ora 02:00–03:00 è ripetuta.'
				: null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle className="font-mono text-base">{puntoMisura}</DialogTitle>
					<DialogDescription>
						{direzione} · POD {pod} · {giorno}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-3">
					<CoverageRow coverage={coverage} />

					{dstNote && (
						<p className="rounded-md bg-bg-secondary px-2.5 py-1.5 text-[11px] text-fg-secondary">
							{dstNote}
						</p>
					)}

					{loading ? (
						<div className="flex h-[320px] items-center justify-center text-fg-secondary text-sm">
							Caricamento della curva…
						</div>
					) : data.length === 0 ? (
						<div className="flex h-[320px] items-center justify-center text-fg-secondary text-sm">
							Nessun dato per questo giorno, su nessun livello.
						</div>
					) : (
						<PodLayersChart data={data} unit={unit} height={320} />
					)}

					{(onPrevDay || onNextDay) && (
						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={onPrevDay}
								disabled={!onPrevDay}
								className="rounded-md border border-border-secondary px-3 py-1.5 text-fg-secondary text-xs hover:text-fg disabled:opacity-40"
							>
								← Giorno precedente
							</button>
							<button
								type="button"
								onClick={onNextDay}
								disabled={!onNextDay || nextDayDisabled}
								className="rounded-md border border-border-secondary px-3 py-1.5 text-fg-secondary text-xs hover:text-fg disabled:opacity-40"
							>
								Giorno successivo →
							</button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
